import { useCallback, useMemo } from 'react'
import { TYPES as DT } from '../context/DataContext'
import { TYPES as UT } from '../context/UiContext'
import { saveRfiData, submitRfiData } from '../../../../services/lcmService'
import { getVettingStatusForSave } from '../../../../common/helper'
import {
  buildServiceErrorsCache,
  consolidateErrorCache,
} from '../../../../common/rfiHelper'
import {
  MESSAGE_VALIDATION_FAILED,
  STATUS_SUBMITTED,
  STATUS_INITIATED,
} from '../../../../common/commonConstants'
import { getTabIdByNameSafe } from '../utils/tabUtils'

export default function useRFISaveSubmit({
  d,
  dd,
  ud,
  manufacturingTypeId,
  rfiType,
  userId,
  auth,
  fetchAll,
  publishRFITimelineData,
  pushDataToFirefly,
  cancelAndRedirect,
}) {
  const saveRfiPayload = useCallback(
    async (payload, sectionId, isRefreshRequired = false) => {
      const tabUid = getTabIdByNameSafe(d.template, d.tabUidByName, d.activeTab)
      const statusToSend = await getVettingStatusForSave(
        String(d?.rfiObject?.vetting_status_code ?? ''),
      )

      const savePayload = {
        manufacturing_type_id: manufacturingTypeId,
        manufacturing_type: d?.rfiObject?.manufacturing_type,
        supplier_id: d?.rfiObject?.supplier_id,
        location_id: d?.rfiObject?.location_id,
        [tabUid]: { [sectionId]: payload },
        user_id: userId,
        vetting_status: statusToSend?.vetting_status,
        vetting_status_code: statusToSend?.vetting_status_code,
        initiated_by:
          statusToSend?.vetting_status_code === 1
            ? auth?.session?.userInfo?.fullName
            : null,
      }

      const updatedTab = {
        ...(d?.rfiObject?.dynamic_sections?.[tabUid] || {}),
        [sectionId]: payload,
      }

      try {
        ud({ type: UT.SET_LOADING, payload: { save: true, saveFailed: false } })
        await saveRfiData(savePayload, rfiType, true)

        if (statusToSend?.vetting_status_code !== undefined) {
          publishRFITimelineData(statusToSend?.vetting_status_code)
        }

        if (!isRefreshRequired) {
          dd({
            type: DT.SET_DATA,
            payload: {
              ...d.rfiObject,
              dynamic_sections: {
                ...d.rfiObject?.dynamic_sections,
                [tabUid]: updatedTab,
              },
              vetting_status: statusToSend?.vetting_status,
              vetting_status_code: statusToSend?.vetting_status_code,
            },
          })
          if (statusToSend?.vetting_status_code != null) {
            dd({
              type: DT.SET_DATA_FROM_API,
              payload: {
                ...d.rfiDataFromApi,
                vetting_status_code: statusToSend?.vetting_status_code,
              },
            })
          }
        } else {
          await fetchAll()
        }

        const newCache = consolidateErrorCache(sectionId, d.errorCache)
        if (newCache) dd({ type: DT.SET_ERRORCACHE, payload: newCache })
        ud({
          type: UT.SET_LOADING,
          payload: { save: false, saveFailed: false },
        })
      } catch (err) {
        const sectionErrors = buildServiceErrorsCache(err)
        const merged = consolidateErrorCache(
          sectionId,
          d.errorCache,
          sectionErrors,
        )
        if (merged) dd({ type: DT.SET_ERRORCACHE, payload: merged })
        if (
          err?.response?.status === 400 &&
          String(err?.response?.data?.message || '').toLowerCase() ===
            MESSAGE_VALIDATION_FAILED
        ) {
          dd({
            type: DT.SET_DATA,
            payload: {
              ...d.rfiObject,
              dynamic_sections: {
                ...d.rfiObject?.dynamic_sections,
                [tabUid]: updatedTab,
              },
            },
          })
        }
        ud({ type: UT.SET_LOADING, payload: { save: false, saveFailed: true } })
      }
    },
    [
      d,
      dd,
      ud,
      manufacturingTypeId,
      rfiType,
      userId,
      fetchAll,
      publishRFITimelineData,
    ],
  )

  const refineRfiSubmitPayload = useCallback(() => {
    const refined = {}
    Object.entries(d?.rfiObject?.dynamic_sections || {}).forEach(
      ([tabUid, node]) => {
        refined[tabUid] = {}
        Object.entries(node || {}).forEach(([sectionUid, sectionData]) => {
          if (d.allSectionUids.has(sectionUid)) {
            refined[tabUid][sectionUid] = sectionData
          }
        })
      },
    )
    return refined
  }, [d?.rfiObject, d.allSectionUids])

  const submitRfiForm = useCallback(
    async (isFinalSubmit = false) => {
      const body = {
        manufacturing_type_id: manufacturingTypeId,
        manufacturing_type: d?.rfiObject?.manufacturing_type,
        supplier_id: d?.rfiObject?.supplier_id,
        location_id: d?.rfiObject?.location_id,
        ...refineRfiSubmitPayload(),
      }
      try {
        ud({
          type: UT.SET_LOADING,
          payload: { submit: true, submitFailed: false },
        })
        await submitRfiData(body, isFinalSubmit, true, rfiType)
        if (!isFinalSubmit) {
          ud({ type: UT.SET_MODAL, payload: { email: true } })
        } else {
          ud({
            type: UT.SET_LOADING,
            payload: { submit: false, submitFailed: false },
          })
          // match old behaviour: timeline + firefly on submit
          pushDataToFirefly(STATUS_SUBMITTED, STATUS_INITIATED)
          publishRFITimelineData(2)
          dd({ type: DT.SET_ERRORCACHE, payload: {} })
          cancelAndRedirect()
        }
      } catch (err) {
        const cache = buildServiceErrorsCache(err)
        if (cache) {
          dd({ type: DT.SET_ERRORCACHE, payload: cache })
          ud({ type: UT.SET_MODAL, payload: { submitValidation: true } })
        }
        ud({
          type: UT.SET_LOADING,
          payload: { submit: false, submitFailed: true },
        })
        ud({ type: UT.SET_SUBMIT_FAILED, payload: true })
      }
    },
    [
      d?.rfiObject,
      manufacturingTypeId,
      rfiType,
      refineRfiSubmitPayload,
      cancelAndRedirect,
      ud,
      dd,
      pushDataToFirefly,
      publishRFITimelineData,
    ],
  )

  const submitHooks = useMemo(
    () => ({
      rfiFinalConfirmationActionClickHandler: (isBack = false) => {
        if (isBack) {
          ud({
            type: UT.SET_MODAL,
            payload: { confirmation: false, email: true },
          })
        } else {
          ud({ type: UT.SET_MODAL, payload: { confirmation: false } })
          submitRfiForm(true)
          ud({ type: UT.SET_FLAGS, payload: { isSaveAndCloseClicked: false } })
        }
      },
      rfiSaveAndContinueActionClickHandler: (isOk = true) => {
        ud({ type: UT.SET_MODAL, payload: { confirmation: false } })
        ud({
          type: UT.SET_LOADING,
          payload: { submit: false, submitFailed: false },
        })
        if (isOk) cancelAndRedirect()
      },
      closeEmailDialog: (launchConfirm, isSaveAndClose = false) => {
        ud({ type: UT.SET_MODAL, payload: { email: false } })
        if (isSaveAndClose || !launchConfirm) {
          ud({
            type: UT.SET_LOADING,
            payload: { submit: false, submitFailed: false },
          })
          ud({
            type: UT.SET_FLAGS,
            payload: { isSaveAndCloseClicked: false },
          })
        } else {
          ud({ type: UT.SET_MODAL, payload: { confirmation: true } })
          ud({
            type: UT.SET_FLAGS,
            payload: { isSaveAndCloseClicked: false },
          })
        }
      },
    }),
    [submitRfiForm, cancelAndRedirect, ud],
  )

  return {
    saveRfiPayload,
    submitRfiForm,
    submitHooks,
  }
}
